import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTrending, type TrendingResponse } from '../../../api/dashboardApi';
import { BRAND_BLUE_SOFT, BRAND_ORANGE } from '../../../design-system/chartColors';

/**
 * Trending regulations (SRS #25) with correct navigation (SRS #26).
 *
 * Every item is a regulation that moved recently — a legislative action, a
 * new discovery, or alerts it triggered — ranked with the account's own
 * relevance. Clicking an item opens that regulation; clicking a
 * jurisdiction filters the All Regulations view. Nothing here opens the
 * chat, which is what the previous panel did.
 */

interface Props {
  token: string | null;
  theme: string;
  onJurisdiction?: (name: string) => void;
}

const day = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const TrendingPanel: React.FC<Props> = ({ token, theme, onJurisdiction }) => {
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [data, setData] = useState<TrendingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    fetchTrending(token)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load trending'); });
    return () => { cancelled = true; };
  }, [token]);

  const muted = isDark ? '#98a2b3' : '#667085';
  const text = isDark ? '#f2f4f7' : '#101828';
  const border = isDark ? 'rgba(255,255,255,0.12)' : '#eaecf0';
  const card: React.CSSProperties = {
    padding: '14px 16px', borderRadius: 12, background: isDark ? '#101828' : 'var(--ds-surface)',
    border: `1px solid ${border}`, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6,
  };
  const chip: React.CSSProperties = {
    fontSize: 10.5, padding: '2px 7px', borderRadius: 999,
    background: isDark ? 'rgba(68,76,231,0.18)' : '#eef2ff', color: isDark ? BRAND_BLUE_SOFT : BRAND_ORANGE,
  };

  if (error) return <p style={{ color: muted, fontStyle: 'italic' }}>{error}</p>;
  if (!data) return <p style={{ color: muted, fontStyle: 'italic' }}>Loading trending regulations…</p>;

  const scopeLabel = data.scope === null ? 'all jurisdictions' : data.scope.length ? data.scope.join(', ') : 'no jurisdictions';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: '1.1rem', color: text }}>Trending in {scopeLabel}</h2>
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: muted }}>
          Regulations with legislative action, new tracking or alerts in the last {data.window_days} days, ranked by relevance to you.
          {data.window_widened && ' The last 90 days were quiet in your scope, so this shows the last year.'}
        </p>
      </div>

      {(data.topics.jurisdictions.length > 0 || data.topics.types.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: muted, marginBottom: 6 }}>Where it is moving</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {data.topics.jurisdictions.map((t) => (
                <button key={t.label} type="button" onClick={() => onJurisdiction?.(t.label)}
                        title={t.change_pct === null ? `${t.current} in window` : `${t.current} vs ${t.previous} in the previous window`}
                        style={{ ...chip, border: 'none', cursor: onJurisdiction ? 'pointer' : 'default', fontSize: 12, padding: '4px 10px' }}>
                  {t.label} · {t.current}{t.change_pct !== null && (t.change_pct >= 0 ? ` ↑${t.change_pct}%` : ` ↓${Math.abs(t.change_pct)}%`)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: muted, marginBottom: 6 }}>What kind</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {data.topics.types.map((t) => (
                <span key={t.label} style={{ ...chip, fontSize: 12, padding: '4px 10px', background: isDark ? 'rgba(255,255,255,0.06)' : '#f2f4f7', color: text }}
                      title={t.change_pct === null ? `${t.current} in window` : `${t.current} vs ${t.previous} previously`}>
                  {t.label} · {t.current}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {data.items.length === 0 ? (
        <p style={{ color: muted, fontStyle: 'italic' }}>
          Nothing has moved in your scope in the last {data.window_days} days. Trending fills in as the regulatory feeds refresh.
        </p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: 14 }}>
          {data.items.map((r, i) => (
            <div key={r.id} style={card} role="link" tabIndex={0}
                 onClick={() => navigate(`/regulations/${r.id}`)}
                 onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/regulations/${r.id}`); }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11, color: muted }}>
                <span>#{i + 1} · {r.jurisdiction_name}{r.regulation_type ? ` · ${r.regulation_type}` : ''}</span>
                <span title="Trending score">{Math.round(r.trending * 100)}</span>
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: text, lineHeight: 1.35 }}>{r.title}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {r.signals.map((sg) => <span key={sg} style={chip}>{sg}</span>)}
              </div>
              {(r.last_action_date || r.first_seen_at) && (
                <div style={{ fontSize: 11, color: muted }}>
                  {r.last_action_date ? `Last action ${day(r.last_action_date)}` : `Tracked since ${day(r.first_seen_at)}`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrendingPanel;
